import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Stack,
  MenuItem,
} from '@mui/material';
import Card from './Card';
import FormGroup from './FormGroup';
import Button from './Button';
import Alert from './Alert';
import api from '../services/api';
import { SKILL_ATTRIBUTES } from '../constants/skillAttributes';

const PAGE_TYPE_OPTIONS = [
  { value: 'detail', label: 'Detail Page' },
  { value: 'search', label: 'Search Page' },
  { value: 'category', label: 'Category Page' },
  { value: 'store', label: 'Store Page' },
];

function SkillOnboardingForm() {
  const [retailer, setRetailer] = useState('');
  const [pageType, setPageType] = useState('detail');
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);
    setValidationError(null);

    const trimmedRetailer = retailer.trim();
    if (!trimmedRetailer) {
      setValidationError('Retailer is required.');
      return;
    }
    if (!pageType) {
      setValidationError('Page type is required.');
      return;
    }
    if (!attributes || attributes.length === 0) {
      setValidationError('Select at least one attribute to onboard.');
      return;
    }

    setLoading(true);

    try {
      const response = await api.onboardSkills({
        retailer: trimmedRetailer,
        pageType,
        attributes: attributes.map((a) => a.value),
      });
      const queued = Array.isArray(response?.queued) ? response.queued : [];
      const skipped = Array.isArray(response?.skipped) ? response.skipped : [];
      const already = skipped.filter((s) => s.reason === 'already_onboarded').length;
      const alreadyQueued = skipped.filter((s) => s.reason === 'already_queued').length;

      const parts = [];
      if (queued.length > 0) {
        parts.push(`queued ${queued.length} attribute${queued.length === 1 ? '' : 's'} for background generation`);
      }
      if (already > 0) {
        parts.push(`${already} already onboarded`);
      }
      if (alreadyQueued > 0) {
        parts.push(`${alreadyQueued} already generating`);
      }
      const scope = `${trimmedRetailer} / ${pageType}`;
      setSuccess(
        parts.length > 0
          ? `Onboarding accepted for ${scope}: ${parts.join(' · ')}. Check the skills list for progress.`
          : `Onboarding accepted for ${scope}. Check the skills list for progress.`,
      );
    } catch (err) {
      setError(err.message || 'Failed to onboard skills.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
        Onboard retailer skills
      </Typography>

      {validationError && (
        <Alert variant="warning" onClose={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}
      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}{' '}
          <Link to="/skills" style={{ color: '#93c5fd', fontWeight: 600 }}>
            View skills
          </Link>
        </Alert>
      )}

      <Card title="New onboarding request" gradient sx={{ mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <FormGroup label="Retailer" required>
            <TextField
              fullWidth
              value={retailer}
              onChange={(e) => setRetailer(e.target.value)}
              placeholder="e.g. walmart"
              disabled={loading}
            />
          </FormGroup>

          <FormGroup label="Page Type" required>
            <TextField
              select
              fullWidth
              value={pageType}
              onChange={(e) => setPageType(e.target.value)}
              disabled={loading}
            >
              {PAGE_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </FormGroup>

          <FormGroup label="Attributes" required>
            <Autocomplete
              multiple
              options={SKILL_ATTRIBUTES}
              value={attributes}
              onChange={(_e, newValue) => setAttributes(newValue)}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              disabled={loading}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                    key={option.value}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Select attributes to onboard" />
              )}
            />
          </FormGroup>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Button type="submit" disabled={loading}>
              {loading ? 'Queueing…' : 'Onboard skills'}
            </Button>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
              Generation runs in the background — you can leave this page after submit.
            </Typography>
          </Stack>
        </form>
      </Card>
    </Box>
  );
}

export default SkillOnboardingForm;
